import * as React from "react";
import { observer } from "mobx-react";

import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";

import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";
import {
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
  UitkLayoutGrid,
} from "uitk-react-layout-grid";
import { NoRehydratationValues } from "components/shared/TravelersField/typings";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { SubmitButton } from "components/shared/SubmitButton/SubmitButton";

import { WizardHotelPWAv2Props } from "../typings";
import { SearchSummaryCard } from "../components/SearchSummaryCard";

export const WizardHotelPWAv2HotwireView: React.FunctionComponent<WizardHotelPWAv2Props> = observer((props) => {
  const {
    hotelWizardState,
    hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
  } = props.wizardState;
  const {
    flexComponent: {
      model: { destinationFromContext = false },
    },
  } = props;

  const { elementId } = config;

  const [isMobileSummary, setMobileSummary] = React.useState(destinationFromContext);
  const expandMobileSummary = () => setMobileSummary(false);

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 4, large: 12 };

  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 4, large: 3 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 4, medium: 2, large: 3 };
  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 4, medium: 2, large: 3 };
  const submitColSpan: UitkLayoutConditionalGridSpan = { small: 4, medium: 1, large: 2 };

  /**
   * Values used if no re-hydration (JS disabled)
   */
  const noRehydrationValues: NoRehydratationValues = {
    numOfTravelers: 2,
    name: "adults",
  };

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    hotelWizardState.wizardInputsArray.push(ref);
    if (secondRef) {
      hotelWizardState.wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsToken
        : config.form.submitButton.locWarningToken;
    }

    return moreThanOneError()
      ? config.form.submitButton.locMultipleWarningsLinkToken
      : config.form.submitButton.locWarningLinkToken;
  };

  /**
   * Render the form elements so that the submit button, which is shown even when
   * the mobile search summary card is shown, always links to the default search.
   */
  const renderForm = (isFormVisible: boolean) => {
    const contents = (
      <>
        {numberOfErrors > 0 && (
          <LobErrorSummary
            locHeadingToken={getLocError(false)!}
            locHeadingArgs={moreThanOneError() && [numberOfErrors]}
            locLinkToken={getLocError(true)}
            linkClickFocusId="roundtrip-error"
            inputErrorRef={errorInputRef}
            colSpan={mainGridColumns}
            setInputsRefs={setInputsRefs}
          />
        )}
        <Location
          originField={false}
          destinationField={true}
          lobState={hotelWizardState}
          colSpan={locationColSpan}
          setInputsRefs={setInputsRefs}
        />
        <Dates
          useHiddenInputs={false}
          startId="startDate"
          endId="endDate"
          lobState={hotelWizardState}
          colSpan={datesColSpan}
          setInputsRefs={setInputsRefs}
        />
        <Travelers lobState={hotelWizardState} colSpan={travelersColSpan} noRehydratationValues={noRehydrationValues} />
      </>
    );

    return (
      <form
        noValidate={true}
        className="WizardHotelPWAHotwire"
        id={elementId}
        data-fm={props.flexComponent.fmId}
        data-fm-title-id={props.flexComponent.fmTitleId}
        onSubmit={hotelWizardState.submit}
        action={hotelWizardState.formAction}
      >
        <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
          {isFormVisible ? contents : <div style={{ display: "none" }}>{contents}</div>}
          <SubmitButton lobState={hotelWizardState} colSpan={submitColSpan} />
        </UitkLayoutGrid>
      </form>
    );
  };

  return (
    <Viewport>
      <ViewSmall>
        <>
          {isMobileSummary && (
            <SearchSummaryCard
              context={props.context}
              hotelWizardState={hotelWizardState}
              onExpand={expandMobileSummary}
            />
          )}
          {renderForm(!isMobileSummary)}
        </>
      </ViewSmall>
      <ViewMedium>{renderForm(true)}</ViewMedium>
    </Viewport>
  );
});

export default WizardHotelPWAv2HotwireView;
